import { Box, Typography } from "@mui/material";
import { ChevronRightRounded } from "@mui/icons-material";
import { focusRingSx } from "../../overviewPanel";
import { CompanyIdentityRow } from "../../../polish/CompanyIdentityRow";
import { cardSx, type TonalTokens } from "../../../polish/tokens";
import { cardHeadingSx } from "../feed/headings";
import { periodTitle, reportLine, shortDate } from "../feed/feedModel";
import type { FeedCompany, PreviousReport } from "../feed/types";

interface PreviousReportsProps {
  previous: PreviousReport[];
  company: FeedCompany;
  tokens: TonalTokens;
  onOpenReport: (eventId: string) => void;
}

/** The company's earlier reports, a row each: the company, then the quarter; each opens its own page. */
export const PreviousReports = ({
  previous,
  company,
  tokens,
  onOpenReport,
}: PreviousReportsProps) => (
  <Box
    component="section"
    aria-labelledby="previous-reports-title"
    data-testid="previous-reports"
    sx={{ ...cardSx(tokens), px: { xs: 2, md: 3 }, py: { xs: 2, md: 2.5 } }}
  >
    <Typography
      id="previous-reports-title"
      component="h2"
      sx={{ ...cardHeadingSx(tokens), mb: 1 }}
    >
      Previous reports
    </Typography>
    <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
      {previous.map((report) => {
        // The period's name, else the report's date: never a guessed quarter.
        const title = periodTitle(
          report.period?.label ?? shortDate(report.reportDate),
        );
        const line = reportLine(report.reportDate, report.callTime);
        return (
          <Box
            component="li"
            key={report.eventId}
            sx={{ borderTop: `1px solid ${tokens.hairline}` }}
          >
            <Box
              component="button"
              type="button"
              data-testid="previous-report"
              aria-label={`${title}, ${line}. Open the report`}
              onClick={() => onOpenReport(report.eventId)}
              sx={{
                all: "unset",
                ...focusRingSx,
                boxSizing: "border-box",
                display: "flex",
                alignItems: "center",
                gap: 2,
                width: "calc(100% + 16px)",
                minHeight: 60,
                mx: -1,
                px: 1,
                py: 1,
                borderRadius: "8px",
                cursor: "pointer",
                WebkitTapHighlightColor: "transparent",
                transition: "background-color 120ms ease",
                "@media (hover: hover)": {
                  "&:hover": { backgroundColor: tokens.raised },
                },
                "@media (prefers-reduced-motion: reduce)": {
                  transition: "none",
                },
              }}
            >
              <Box sx={{ flex: "0 1 auto", minWidth: 0 }}>
                <CompanyIdentityRow
                  symbol={company.symbol}
                  name={company.companyName}
                />
              </Box>
              <Box
                aria-hidden
                sx={{
                  flex: "none",
                  width: "1px",
                  height: 32,
                  backgroundColor: tokens.hairline,
                }}
              />
              <Typography
                data-testid="previous-period"
                noWrap
                sx={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: { xs: "0.9375rem", md: "1rem" },
                  fontWeight: 600,
                  color: tokens.text,
                }}
              >
                {title}
              </Typography>
              <ChevronRightRounded
                aria-hidden
                sx={{ fontSize: 20, color: tokens.faint, flex: "none" }}
              />
            </Box>
          </Box>
        );
      })}
    </Box>
  </Box>
);
